import { type Artifact, type DefinitionSet, type Layer, entityDef } from "./defs"
import { asArray, asString, isRecord } from "./utils"

/** One property on a type node; `extension` names the tenant extension that added it. */
export interface TypeProperty {
  readonly id: string
  readonly type: string
  readonly extension?: string
}

/** One object type or interface in the type graph, with extension members merged in. */
export interface TypeNode {
  readonly id: string
  readonly artifactId: string
  readonly form: "objectType" | "interface"
  readonly layer: Layer
  readonly description: string | undefined
  readonly properties: readonly TypeProperty[]
  /** Namespaces of the extensions merged onto this type. */
  readonly extendedBy: readonly string[]
}

export interface TypeEdge {
  readonly id: string
  readonly source: string
  readonly target: string
  readonly kind: "link" | "implements" | "extends"
  readonly label: string
  /** The layer of the artifact that declared the edge (an extension's links are tenant edges). */
  readonly layer: Layer
}

export interface TypeGraph {
  readonly nodes: readonly TypeNode[]
  readonly edges: readonly TypeEdge[]
  /** Types left out by the bound (registry types not adjacent to a tenant type). */
  readonly hidden: number
}

function typeLabel(type: unknown): string {
  if (!isRecord(type)) return "?"
  const kind = asString(type.kind) ?? "?"
  const ref = asString(type.name) ?? asString(type.ref) ?? asString(type.target)
  return ref ? ref : kind
}

function linkEdges(source: string, links: unknown, layer: Layer, origin: string): TypeEdge[] {
  return asArray(links)
    .filter(isRecord)
    .flatMap((link) => {
      const target = asString(link.target)
      const id = asString(link.id)
      if (!target || !id) return []
      const card = isRecord(link.cardinality) ? link.cardinality : {}
      return [{ id: `link:${origin}:${source}:${id}`, source, target, kind: "link" as const, label: `${id} · ${asString(card.from) ?? "?"}→${asString(card.to) ?? "?"}`, layer }]
    })
}

/**
 * The declared type graph: object types and interfaces as nodes, links,
 * `implements` and `extends` as edges, and every `objectTypeExtension`'s
 * properties and links merged onto its target type. Bounded by default to
 * tenant types, types a tenant extension customizes, and the types one hop
 * from those; `includeRegistry` draws all.
 */
export function buildTypeGraph(set: DefinitionSet, options: { includeRegistry: boolean }): TypeGraph {
  type MutableNode = Omit<TypeNode, "properties" | "extendedBy"> & { properties: TypeProperty[]; extendedBy: string[] }
  const all = new Map<string, MutableNode>()
  const edges: TypeEdge[] = []
  const extensions: { artifact: Artifact; body: Record<string, unknown> }[] = []
  for (const a of set.artifacts) {
    if (a.kind !== "entity") continue
    const def = entityDef(a)
    if (!def) continue
    if (def.form === "extension") {
      extensions.push({ artifact: a, body: def.body })
      continue
    }
    if (def.form !== "objectType" && def.form !== "interface") continue
    const props = asArray(def.body.properties).filter(isRecord)
    all.set(def.name, {
      id: def.name,
      artifactId: a.artifactId,
      form: def.form,
      layer: a.layer,
      description: def.description,
      properties: props.map((p) => ({ id: asString(p.id) ?? "?", type: typeLabel(p.type) })),
      extendedBy: [],
    })
    edges.push(...linkEdges(def.name, def.body.links, a.layer, a.artifactId))
    for (const iface of asArray(def.body.implements)) {
      if (typeof iface === "string") edges.push({ id: `impl:${def.name}:${iface}`, source: def.name, target: iface, kind: "implements", label: "implements", layer: a.layer })
    }
    const ext = asString(def.body.extends)
    if (ext) edges.push({ id: `ext:${def.name}:${ext}`, source: def.name, target: ext, kind: "extends", label: "extends", layer: a.layer })
  }
  const customized = new Set<string>()
  for (const { artifact, body } of extensions) {
    const target = all.get(asString(body.target) ?? "")
    if (!target) continue
    const namespace = asString(body.namespace) ?? artifact.artifactId
    target.extendedBy.push(namespace)
    for (const p of asArray(body.properties).filter(isRecord)) target.properties.push({ id: asString(p.id) ?? "?", type: typeLabel(p.type), extension: namespace })
    edges.push(...linkEdges(target.id, body.links, artifact.layer, artifact.artifactId))
    if (artifact.layer === "tenant") customized.add(target.id)
  }
  let keep: Set<string>
  if (options.includeRegistry) {
    keep = new Set(all.keys())
  } else {
    const tenant = new Set([...[...all.values()].filter((n) => n.layer === "tenant").map((n) => n.id), ...customized])
    keep = new Set(tenant)
    // Exactly one hop from a tenant (or tenant-customized) type, in either direction; never transitive.
    for (const e of edges) {
      if (tenant.has(e.source) && all.has(e.target)) keep.add(e.target)
      if (tenant.has(e.target) && all.has(e.source)) keep.add(e.source)
    }
    if (keep.size === 0) keep = new Set(all.keys())
  }
  const nodes = [...all.values()].filter((n) => keep.has(n.id))
  return {
    nodes,
    edges: edges.filter((e) => keep.has(e.source) && keep.has(e.target)),
    hidden: all.size - nodes.length,
  }
}

/**
 * A type's effective property ids, in presentation order: its own, then each supertype's along
 * `extends`, then the properties every `objectTypeExtension` adds to it or to a supertype.
 */
export function effectiveProperties(set: DefinitionSet, typeName: string): string[] {
  const types = new Map<string, Record<string, unknown>>()
  const extensions: Record<string, unknown>[] = []
  for (const a of set.artifacts) {
    if (a.kind !== "entity") continue
    const def = entityDef(a)
    if (def?.form === "objectType") types.set(def.name, def.body)
    else if (def?.form === "extension") extensions.push(def.body)
  }
  const out: string[] = []
  const seen = new Set<string>()
  const add = (props: unknown) => {
    for (const p of asArray(props).filter(isRecord)) {
      const id = asString(p.id)
      if (!id || seen.has(id)) continue
      seen.add(id)
      out.push(id)
    }
  }
  const chain: string[] = []
  for (let name: string | undefined = typeName; name && types.has(name) && !chain.includes(name); name = asString(types.get(name)?.extends)) {
    chain.push(name)
    add(types.get(name)?.properties)
  }
  for (const ext of extensions) if (chain.includes(asString(ext.target) ?? "")) add(ext.properties)
  return out
}

/** One row of a declared mapping: a target property and the ZEN expression that fills it. */
export interface MappingRow {
  readonly property: string
  readonly expression: string
  readonly inputs: readonly string[]
  readonly lifecycle: boolean
}

export interface DeclaredMap {
  readonly id: string
  /** The source dataset this map reads: `<schemaArtifactId>#<dataset>`. */
  readonly sourceId: string
  readonly mappingArtifactId: string
  readonly dataset: string
  readonly targetType: string
  readonly targetArtifactId: string
  readonly rows: readonly MappingRow[]
}

export interface SourceDataset {
  readonly id: string
  readonly schemaArtifactId: string
  readonly system: string
  /** True only for the console fallback, never a declared system identifier. */
  readonly systemFallback?: boolean
  readonly fields: readonly { readonly name: string; readonly nativeType: string }[]
}

export interface DeclaredLineage {
  readonly sources: readonly SourceDataset[]
  readonly maps: readonly DeclaredMap[]
  readonly targets: readonly { readonly type: string; readonly artifactId: string; readonly properties: readonly string[] }[]
}

// The forms the mapping compiler admits: `input.field`, `input['field']`, `input["field"]`.
const INPUT_REF = /\binput(?:\.([A-Za-z_][A-Za-z0-9_]*)|\[\s*'([^']*)'\s*\]|\[\s*"([^"]*)"\s*\])/g

/** Source fields an expression reads (`input.grand_total` → `grand_total`), in first-use order. */
export function inputFields(expression: string): string[] {
  return [...new Set([...expression.matchAll(INPUT_REF)].map((m) => m[1] ?? m[2] ?? m[3] ?? ""))].filter(Boolean)
}

/**
 * Source → MappingIR → object type, as declared by the installed mapping and
 * source-schema artifacts. This is what the definitions say will happen; what
 * did happen is the recorded lineage of `why`.
 */
export function buildDeclaredLineage(set: DefinitionSet): DeclaredLineage {
  const byId = new Map<string, Artifact>(set.artifacts.map((a) => [a.artifactId, a]))
  const sources = new Map<string, SourceDataset>()
  const maps: DeclaredMap[] = []
  const targets = new Map<string, { type: string; artifactId: string; properties: string[] }>()

  for (const m of set.artifacts.filter((a) => a.kind === "mapping")) {
    const sourceRef = isRecord(m.payload.source) ? m.payload.source : {}
    const schema = byId.get(asString(sourceRef.schemaId) ?? "")
    for (const map of asArray(m.payload.maps).filter(isRecord)) {
      const dataset = isRecord(map.source) ? (asString(map.source.dataset) ?? "?") : "?"
      const targetArtifactId = isRecord(map.target) ? (asString(map.target.entity) ?? "?") : "?"
      const entity = byId.get(targetArtifactId)
      const def = entity ? entityDef(entity) : null
      const targetType = def?.name ?? targetArtifactId
      const rows: MappingRow[] = [
        ...asArray(map.properties)
          .filter(isRecord)
          .map((p) => {
            const expression = asString(p.value) ?? JSON.stringify(p.value)
            return { property: asString(p.property) ?? "?", expression, inputs: inputFields(expression), lifecycle: false }
          }),
        ...asArray(map.lifecycles)
          .filter(isRecord)
          .map((l) => {
            // A lifecycle mapping declares either the target `state` or the `transition` to fire.
            const expression = asString(l.state) ?? asString(l.transition) ?? ""
            return { property: asString(l.property) ?? "?", expression, inputs: inputFields(expression), lifecycle: true }
          }),
      ]
      const sourceKey = `${schema?.artifactId ?? "?"}#${dataset}`
      maps.push({ id: `${m.artifactId}#${asString(map.id) ?? dataset}`, sourceId: sourceKey, mappingArtifactId: m.artifactId, dataset, targetType, targetArtifactId, rows })

      if (!sources.has(sourceKey)) {
        const fields = schema
          ? asArray(schema.payload.fields)
              .filter(isRecord)
              .filter((f) => f.dataset === dataset)
              .map((f) => ({ name: asString(f.name) ?? "?", nativeType: asString(f.nativeType) ?? "" }))
          : []
        const declaredSystem = schema && isRecord(schema.payload.source) ? asString(schema.payload.source.system) : undefined
        const system = declaredSystem ?? "source"
        // Fields a mapping reads but the schema omits still appear, so no edge dangles.
        const known = new Set(fields.map((f) => f.name))
        for (const row of rows) {
          for (const f of row.inputs) {
            if (known.has(f)) continue
            known.add(f)
            fields.push({ name: f, nativeType: "" })
          }
        }
        sources.set(sourceKey, { id: sourceKey, schemaArtifactId: schema?.artifactId ?? "?", system, fields, ...(declaredSystem === undefined ? { systemFallback: true } : {}) })
      }
      if (!targets.has(targetType)) {
        // Effective members, so a mapping into an inherited or extension property has a target handle.
        targets.set(targetType, { type: targetType, artifactId: targetArtifactId, properties: def ? effectiveProperties(set, def.name) : [] })
      }
    }
  }
  return { sources: [...sources.values()], maps, targets: [...targets.values()] }
}
